import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaFacebookF, FaWhatsapp, FaLinkedinIn, FaWeixin } from 'react-icons/fa'
import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '../../lib/utils'
import wechatQr from '../../assets/images/broxel-wechat-qr.jpeg'

interface SocialMediaLinksProps {
  variant?: 'default' | 'colored'
  size?: 'sm' | 'md' | 'lg'
  className?: string
  /**
   * How to surface WeChat (it has no web URL, only a QR):
   * - 'inline': always-visible large QR below the icon row (contact pages)
   * - 'modal': a WeChat icon in the row that opens a QR modal (footer)
   * - 'none': hide WeChat entirely
   */
  wechat?: 'inline' | 'modal' | 'none'
}

const sizeClasses = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6'
} as const

const SocialMediaLinks: React.FC<SocialMediaLinksProps> = ({
  variant = 'default',
  size = 'md',
  className = '',
  wechat = 'inline'
}) => {
  const { t } = useTranslation()
  const iconSize = sizeClasses[size]
  const isColored = variant === 'colored'

  const wechatLabel = t('contact.social.wechat')
  const wechatCaption = t('contact.social.wechatScan')

  const socialLinks = [
    {
      name: 'facebook',
      icon: FaFacebookF,
      href: 'https://www.facebook.com/share/18bDyVjnV7/?mibextid=wwXIfr',
      colorBg: 'bg-[#1877F2]',
      colorHover: 'hover:bg-[#166FE5]',
      label: t('contact.social.facebook')
    },
    {
      name: 'linkedin',
      icon: FaLinkedinIn,
      href: 'https://www.linkedin.com/company/broxelpharma/',
      colorBg: 'bg-[#0A66C2]',
      colorHover: 'hover:bg-[#004182]',
      label: t('contact.social.linkedin')
    },
    {
      name: 'whatsapp',
      icon: FaWhatsapp,
      href: 'https://wa.me/201118321999',
      colorBg: 'bg-[#25D366]',
      colorHover: 'hover:bg-[#20BD5A]',
      label: t('contact.social.whatsapp')
    }
  ]

  const buttonClass = (colorBg: string, colorHover: string) =>
    isColored
      ? `p-3 ${colorBg} ${colorHover} rounded-lg transition-colors`
      : 'text-muted-foreground hover:text-primary hover:scale-110 transition-all duration-200 p-2 rounded-full hover:bg-primary/10'

  const iconClass = isColored ? `${iconSize} text-white` : iconSize

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <div className={isColored ? 'flex gap-3' : 'flex space-x-4'}>
        {socialLinks.map(({ name, icon: Icon, href, colorBg, colorHover, label }) => (
          <a
            key={name}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={buttonClass(colorBg, colorHover)}
            aria-label={label}
          >
            <Icon className={iconClass} />
          </a>
        ))}

        {wechat === 'modal' && (
          <Dialog.Root>
            <Dialog.Trigger asChild>
              <button
                type="button"
                className={buttonClass('bg-[#07C160]', 'hover:bg-[#06AD56]')}
                aria-label={wechatLabel}
              >
                <FaWeixin className={iconClass} />
              </button>
            </Dialog.Trigger>
            <Dialog.Portal>
              <Dialog.Overlay className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm" />
              <Dialog.Content className="fixed left-1/2 top-1/2 z-[100] w-[90vw] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-8 shadow-2xl focus:outline-none">
                <Dialog.Title className="flex items-center justify-center gap-2 text-center text-lg font-semibold text-gray-900">
                  <FaWeixin className="h-5 w-5 text-[#07C160]" />
                  {wechatLabel}
                </Dialog.Title>
                <Dialog.Description className="sr-only">{wechatCaption}</Dialog.Description>
                <img
                  src={wechatQr}
                  alt={wechatLabel}
                  className="mx-auto mt-5 w-full max-w-sm rounded-lg border-4 border-white object-contain shadow-md"
                />
                <p className="mt-3 text-center text-sm text-gray-600">{wechatCaption}</p>
                <Dialog.Close
                  className="absolute right-3 top-3 rounded-full p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </Dialog.Close>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        )}
      </div>

      {wechat === 'inline' && (
        <div className="flex w-full flex-col items-center gap-3 rounded-lg bg-gradient-to-br from-green-50 to-emerald-50 p-4">
          <img
            src={wechatQr}
            alt={wechatLabel}
            className="w-full max-w-[18rem] rounded-lg border-4 border-white object-contain shadow-md"
          />
          <p className="text-center text-xs text-gray-600">{wechatCaption}</p>
        </div>
      )}
    </div>
  )
}

export default SocialMediaLinks
